// =========================================================
// 全局常量、翻译、样式 token
// =========================================================

export const PLATFORM_WA = "50766131328";
export const REFERRAL_REWARD = 5; // USD, credited to both parties on first completed order
export const CLIENT_ORDER_LIMIT = 80;
export const WORKER_MARKET_LIMIT = 60;
export const ADMIN_ORDER_LIMIT = 250;
export const ORDER_RENDER_LIMIT = 50;
export const IMAGE_MAX_SIDE = 900;
export const IMAGE_TARGET_BYTES = 180 * 1024;
export const PLATFORM_COMMISSION_RATE = 0.10;
export const INVOICE_TAX_RATE = 0.07;

export const createEmptyInvoiceDetails = () => ({ name: "", taxId: "", dv: "", email: "", note: "" });

// ---- 金额工具 ----
export const toMoney = (value) => Math.round((Number(value) || 0) * 100) / 100;
export const getOrderCustomerTotal = (order = {}) => toMoney(order.customer_total ?? order.amount ?? 0);
export const getOrderTaxRate = (order = {}) => Number(order.tax_rate) || 0;
export const getOrderServiceSubtotal = (order = {}) => {
  if (order.service_subtotal != null) return toMoney(order.service_subtotal);
  const taxRate = getOrderTaxRate(order);
  const total = getOrderCustomerTotal(order);
  return taxRate > 0 ? toMoney(total / (1 + taxRate)) : total;
};
export const getOrderTaxAmount = (order = {}) => {
  if (order.tax_amount != null) return toMoney(order.tax_amount);
  const taxRate = getOrderTaxRate(order);
  return taxRate > 0 ? toMoney(getOrderCustomerTotal(order) - getOrderServiceSubtotal(order)) : 0;
};
export const calculateOrderSettlement = (order = {}, refundAmount = 0) => {
  const taxRate = getOrderTaxRate(order);
  const customerTotal = getOrderCustomerTotal(order);
  const adjustedTotal = toMoney(Math.max(0, customerTotal - (Number(refundAmount) || 0)));
  const serviceSubtotal = taxRate > 0 ? toMoney(adjustedTotal / (1 + taxRate)) : adjustedTotal;
  const taxAmount = taxRate > 0 ? toMoney(adjustedTotal - serviceSubtotal) : 0;
  const platformFee = toMoney(serviceSubtotal * PLATFORM_COMMISSION_RATE);
  const workerServiceEarnings = toMoney(serviceSubtotal - platformFee);
  const workerPayoutTotal = toMoney(workerServiceEarnings + taxAmount);
  return { customerTotal, adjustedTotal, serviceSubtotal, taxAmount, platformFee, workerServiceEarnings, workerPayoutTotal };
};

// ---- 工具函数 ----
export const normalizeString = (str) => str.trim().toLowerCase().replace(/\s+/g, ' ');
export const normalizeServiceKey = (str) => normalizeString(String(str || ""));
export const generateID = (prefix) => `${prefix}-${Math.floor(100000 + Math.random() * 900000)}`;
export const formatDate = (ts) => ts ? ts.toDate().toLocaleDateString() + ' ' + ts.toDate().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : "";
export const openWhatsApp = (num, msg) => window.open(`https://wa.me/${num.replace(/\D/g, '')}?text=${encodeURIComponent(msg)}`, '_blank');

export const compressImage = (file, callback, lang = 'es', onError = null) => {
  if (!file) return;
  const errFn = onError || (msg => alert(msg));
  if (!file.type?.startsWith('image/')) {
    errFn(lang === 'es' ? 'Por favor sube una imagen válida.' : '请上传有效图片。');
    return;
  }
  const reader = new FileReader(); reader.readAsDataURL(file);
  reader.onload = (e) => {
    const img = new Image(); img.src = e.target.result;
    img.onload = () => {
      const ratio = Math.min(1, IMAGE_MAX_SIDE / Math.max(img.width, img.height));
      const width = Math.max(1, Math.round(img.width * ratio));
      const height = Math.max(1, Math.round(img.height * ratio));
      const canvas = document.createElement('canvas'); canvas.width = width; canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, width, height);
      let quality = 0.72;
      let dataUrl = canvas.toDataURL('image/webp', quality);
      const outputType = dataUrl.startsWith('data:image/webp') ? 'image/webp' : 'image/jpeg';
      dataUrl = canvas.toDataURL(outputType, quality);
      while (dataUrl.length > IMAGE_TARGET_BYTES * 1.37 && quality > 0.42) {
        quality -= 0.08; dataUrl = canvas.toDataURL(outputType, quality);
      }
      callback(dataUrl);
    };
    img.onerror = () => errFn(lang === 'es' ? 'No se pudo leer la imagen.' : '图片读取失败，请换一张重试。');
  };
};

// ---- 状态映射 ----
export const statusMap = {
  es: { PENDING: "Pendiente", PENDING_PAYMENT: "Esperando Pago", AWAITING_VERIFICATION: "Verificando Pago", OPEN: "Disponible en Mercado", ASSIGNED: "Asignado", ACCEPTED: "Técnico en Camino", WORKING: "En Proceso", PENDING_APPROVAL: "Esperando Aprobación", COMPLETED: "Finalizado", REFUNDED: "Reembolsado", DISPUTED: "En Disputa (Retenido)", CANCELLED_TIMEOUT: "Cancelado — sin técnico (Reembolsado)" },
  zh: { PENDING: "待处理", PENDING_PAYMENT: "待支付", AWAITING_VERIFICATION: "支付审核中", OPEN: "抢单大厅待接", ASSIGNED: "已派单", ACCEPTED: "师傅已接单", WORKING: "进行中", PENDING_APPROVAL: "待客户验收", COMPLETED: "已完成", REFUNDED: "超时已退款", DISPUTED: "申诉仲裁中 (资金冻结)", CANCELLED_TIMEOUT: "已取消 — 无人接单（已退款）" }
};

export const getServiceName = (name, currentLang) => {
  if (!name) return "";
  const dict = { "aires acondicionados": "Limpieza de A/C", "electricidad": "Electricista", "limpieza": "Limpieza", "plomería": "Plomero", "plomeria": "Plomero", "otros servicio": "Otros Servicios", "otros servicios": "Otros Servicios" };
  if (currentLang === 'es') return dict[name.toLowerCase().trim()] || name;
  const dictZh = { "aires acondicionados": "空调清洗", "electricidad": "电工", "limpieza": "家庭保洁", "clases particulares": "上门家教", "línea blanca": "家电维修", "linea blanca": "家电维修", "plomería": "水工", "plomeria": "水工", "otros servicio": "其他定制服务", "otros servicios": "其他定制服务" };
  return dictZh[name.toLowerCase().trim()] || name;
};

// ── Zonas de Panamá ────────────────────────────────────────────────────────
export const PANAMA_ZONES = [
  // ── Fase 1: Zonas activas ──────────────────────────────
  { id: 'bella-vista',    label: 'Bella Vista',                  group: 'Centro', active: true },
  { id: 'san-francisco',  label: 'San Francisco',                group: 'Centro', active: true },
  { id: 'marbella',       label: 'Marbella / Obarrio',           group: 'Centro', active: true },
  { id: 'el-cangrejo',    label: 'El Cangrejo',                  group: 'Centro', active: true },
  { id: 'punta-pacifica', label: 'Punta Pacífica / Paitilla',   group: 'Centro', active: true },
  { id: 'via-espana',     label: 'Vía España / Betania',         group: 'Centro', active: true },
  { id: 'costa-del-este', label: 'Costa del Este',               group: 'Este',   active: true },
  // ── Próximamente ──────────────────────────────────────
  { id: 'casco-viejo',    label: 'Casco Viejo',                  group: 'Histórico', active: false },
  { id: 'calidonia',      label: 'Calidonia / Curundú',          group: 'Histórico', active: false },
  { id: 'juan-diaz',      label: 'Juan Díaz',                    group: 'Este',   active: false },
  { id: 'tocumen',        label: 'Tocumen / Pacora',             group: 'Este',   active: false },
  { id: 'las-mananitas',  label: 'Las Mañanitas',                group: 'Este',   active: false },
  { id: 'san-miguelito',  label: 'San Miguelito',                group: 'Norte',  active: false },
  { id: 'las-cumbres',    label: 'Las Cumbres',                  group: 'Norte',  active: false },
  { id: 'chilibre',       label: 'Chilibre',                     group: 'Norte',  active: false },
  { id: 'arraiján',       label: 'Arraiján / Vista Alegre',      group: 'Oeste',  active: false },
  { id: 'la-chorrera',    label: 'La Chorrera',                  group: 'Oeste',  active: false },
  { id: 'panama-pacifico',label: 'Panamá Pacífico',              group: 'Oeste',  active: false },
];

export const SCHEDULE_TIME_SLOTS = [
  { id: 'morning', label_es: 'Mañana  8:00 – 12:00', label_zh: '上午  8:00–12:00' },
  { id: 'afternoon', label_es: 'Tarde  12:00 – 17:00', label_zh: '下午  12:00–17:00' },
  { id: 'evening', label_es: 'Noche  17:00 – 20:00', label_zh: '晚上  17:00–20:00' },
];

// ── 各服务范围 & 规则 ───────────────────────────────────────────────────────
export const SERVICE_SCOPE = {
  'aires acondicionados': {
    es: {
      includes: [
        'Limpieza de filtros internos y externos',
        'Lavado del serpentín del evaporador (unidad interior)',
        'Limpieza de bandeja de condensado',
        'Verificación de temperatura y flujo de aire',
        'Prueba de funcionamiento al finalizar',
      ],
      excludes: [
        'Recarga de refrigerante / gas (cobro aparte)',
        'Reemplazo de piezas o repuestos',
        'Instalación o desmontaje del equipo',
        'Limpieza de unidad exterior (cotizar por separado)',
      ],
      rules: [
        'El equipo debe encender y tener acceso eléctrico disponible',
        'Se requiere espacio libre alrededor de la unidad interior',
      ],
      priceNote: '$45 / unidad',
    },
    zh: {
      includes: [
        '清洗内机过滤网（内外两层）',
        '高压清洗蒸发器铜管（内机）',
        '清洁冷凝水盘',
        '检测出风温度与风量',
        '完工后运行测试',
      ],
      excludes: [
        '添加制冷剂/雪种（另收费）',
        '更换任何零部件',
        '安装或拆机服务',
        '外机清洗（需单独报价）',
      ],
      rules: [
        '设备须能正常通电开机',
        '内机周围须有足够操作空间',
      ],
      priceNote: '$45 / 台',
    },
  },
  'electricidad': {
    es: {
      includes: [
        'Diagnóstico del problema eléctrico (incluido)',
        'Cambio de tomacorrientes, apagadores e interruptores',
        'Revisión y cambio de breakers / disyuntores',
        'Instalación de lámparas, abanicos y luminarias',
        'Reparación de circuitos de baja tensión (110V / 220V)',
      ],
      excludes: [
        'Instalación de tableros o paneles eléctricos nuevos',
        'Tendido de circuitos nuevos (requiere permiso del ETESA)',
        'Trabajo en instalaciones subterráneas o empotradas',
        'Instalación de plantas eléctricas o generadores',
        'Trabajos en postes o acometida exterior',
      ],
      rules: [
        'La tarifa cubre únicamente la mano de obra / visita técnica — sin materiales',
        'Si se necesitan repuestos, el técnico informa el costo antes de comprarlos; el cliente decide',
        'Requiere acceso al tablero / caja de breakers principal',
        'Trabajos mayores o nuevas instalaciones requieren cotización aparte',
      ],
      priceNote: 'Desde $35 · solo mano de obra',
    },
    zh: {
      includes: [
        '电气故障诊断（含在服务费内）',
        '插座、开关、断路器的检修与更换操作',
        '灯具、风扇、照明设备安装',
        '110V / 220V 小型线路修复',
      ],
      excludes: [
        '任何零配件或材料（插座、开关、线材等）',
        '新电气配电箱安装',
        '新线路铺设（须ETESA许可证）',
        '地下或暗埋线路施工',
        '发电机安装',
      ],
      rules: [
        '服务费仅为上门人工费，不含任何材料',
        '如需购买配件，师傅会先告知价格，由客户决定是否购买',
        '需提供主配电箱访问权限',
        '大型工程或新装需另行报价',
      ],
      priceNote: '起价 $35 · 纯人工费',
    },
  },
  'plomería': {
    es: {
      includes: [
        'Diagnóstico de fugas y problemas de plomería',
        'Reparación o cambio de llaves de paso y grifos',
        'Reparación de inodoros, cisternas y flotadores',
        'Destape de tuberías, desagüotes y sifones',
        'Cambio de duchas, flexibles y accesorios visibles',
      ],
      excludes: [
        'Tuberías empotradas en paredes o bajo losa (requiere evaluación)',
        'Instalación de calentadores de agua (cotizar aparte)',
        'Obras de remodelación o apertura de paredes',
        'Instalación de tuberías de PVC en nuevas construcciones',
        'Trabajos en tanques de agua elevados (roof tank)',
      ],
      rules: [
        'La tarifa cubre únicamente la mano de obra / visita técnica — sin materiales',
        'Si se necesitan repuestos (llaves, sellos, tubería), el técnico informa el costo antes; el cliente decide',
        'El cliente debe indicar la ubicación exacta del problema',
        'Se recomienda cerrar la llave de paso principal antes de la visita',
      ],
      priceNote: 'Desde $40 · solo mano de obra',
    },
    zh: {
      includes: [
        '漏水检测与水管故障诊断',
        '水龙头、截止阀的检修与更换操作',
        '马桶、水箱、浮球阀维修',
        '下水道、地漏、存水弯疏通',
        '花洒及可见管件的安装操作',
      ],
      excludes: [
        '任何零配件或材料（水龙头、密封圈、管件等）',
        '墙内或楼板下暗管（需专项评估）',
        '热水器安装（需另报价）',
        '需开墙或破地板的施工',
        '楼顶储水罐施工',
      ],
      rules: [
        '服务费仅为上门人工费，不含任何材料',
        '如需购买配件，师傅会先告知价格，由客户决定是否购买',
        '请提前告知问题具体位置',
        '建议施工前关闭房间总水阀',
      ],
      priceNote: '起价 $40 · 纯人工费',
    },
  },
  'otros servicios': {
    es: {
      includes: [
        'Cotización personalizada antes de iniciar el trabajo',
        'Técnico especialista asignado según el tipo de servicio',
        'Mismo sistema de pago protegido (escrow FixBox)',
        'Garantía de 30 días sobre el trabajo realizado',
      ],
      excludes: [
        'Precio fijo — varía según el alcance real del trabajo',
        'Disponibilidad inmediata garantizada (sujeto a agenda)',
      ],
      rules: [
        'Describe el trabajo con el mayor detalle posible',
        'Puedes adjuntar fotos para que el técnico entienda mejor',
        'El técnico enviará cotización antes de aceptar el pedido',
        'El precio final queda acordado antes de iniciar',
      ],
      priceNote: 'Precio según cotización del técnico',
    },
    zh: {
      includes: [
        '开工前提供个性化定制报价',
        '根据服务类型匹配专业师傅',
        '同样的资金托管（escrow）保障',
        '完工后享有30天质量保修',
      ],
      excludes: [
        '无固定价格（根据实际工作量定价）',
        '无法保证即时响应（视师傅排期）',
      ],
      rules: [
        '请尽量详细描述您的需求',
        '可上传现场照片帮助师傅理解',
        '师傅接单前会先发送报价',
        '双方确认价格后才开始施工',
      ],
      priceNote: '价格以师傅报价为准',
    },
  },
};

export const DEFAULT_PUBLIC_SERVICES = [
  { id: 'S-AC', service_name: 'aires acondicionados', price: 1250, is_active: true, isFallback: true },
  { id: 'S-ELEC', service_name: 'electricidad', price: 35, is_active: true, isFallback: true },
  { id: 'S-PLUMB', service_name: 'plomería', price: 35, is_active: true, isFallback: true },
  { id: 'S-999', service_name: 'otros servicios', price: 0, is_active: true, isFallback: true }
];

export const defaultLegalText = {
  es: `🛡️ 1. Garantía FixBox (Exclusiva Online)\n• Todos los trabajos pagados a través de la plataforma cuentan con 30 días de garantía contra fallas.\n• Si el técnico no resuelve el problema, FixBox enviará a otro especialista sin costo adicional.\n\n👤 2. Seguridad del Pago\n• Su dinero permanece seguro en Escrow (Fideicomiso) hasta que usted apruebe el trabajo.\n• No realice pagos en efectivo por fuera si desea mantener su garantía de 30 días.\n\n⚖️ 3. Responsabilidades del Técnico\n• Debe subir fotos de "Antes" y "Después" para habilitar el cobro.\n• El técnico es el proveedor directo del servicio. Puede registrarse aunque no emita factura fiscal.\n• Si un pedido solicita factura fiscal, el técnico debe revisar el requisito antes de aceptarlo y solo tomarlo si puede cumplir.\n• FixBox cobra una comisión del 10% por gastos operativos y emite solo recibo de servicio, no factura fiscal del trabajo técnico.\n\n📞 4. Soporte Oficial\n• WhatsApp: +507 6613-1328\n• Email: soporte@fixbox.com`,
  zh: `🛡️ 1. FixBox 独家售后保障\n• 所有通过平台支付的订单，均享有 30 天超长售后保修期。\n• 若师傅未修好或出现质量问题，平台将免费派发新师傅重修，客户无需支付额外费用。\n\n👤 2. 资金安全保障\n• 您的付款将被安全冻结在平台。直到您亲自确认验收后，款项才会结算给师傅。\n• ⚠️ 严禁线下现金交易！私下付款将导致您的"30天售后保障"立即失效。\n\n⚖️ 3. 平台规则\n• 师傅必须上传开工和完工照片作为存证。\n• 师傅是实际服务卖方；不会开发票也可以注册和接普通订单。\n• 如果订单标记客户需要正式发票，师傅接单前必须看清楚要求，并确认自己可以按规定处理。\n• FixBox 目前仅收取 10% 运营服务费，并只提供服务凭证，不代替师傅开维修服务发票。\n\n📞 4. 客服中心\n• WhatsApp: +507 6613-1328\n• 邮箱: soporte@fixbox.com`
};

// ---- 翻译 ----
export const translations = {
  es: {
    appName: "FixBox", home: "Inicio", orders: "Pedidos", profile: "Perfil",
    wallet: "Mi Billetera", balance: "Saldo Disponible", withdraw: "Solicitar Retiro",
    withdraw_success: "Solicitud enviada. Pago programado.", withdraw_hist: "Historial de Retiros",
    edit_profile: "Mis Datos", email: "Email de cuenta", phone: "WhatsApp", address: "Dirección",
    save: "Guardar Cambios", rules: "Términos y Reglas", price_label: "Precio:", support_btn: "Soporte",
    btn_order: "Ir a Pagar", pay_select: "Método de Pago", pay_yappy: "Yappy (Recomendado)", pay_wallet: "Mi Billetera (Saldo)",
    pay_done: "Confirmar Pago", photos_title: "Evidencias (Máx 6)",
    suggest_title: "¿Qué servicio necesita?", suggest_btn: "Enviar Solicitud", suggest_success: "¡Gracias, hemos recibido tu solicitud y te contactaremos pronto!",
    legal_p: "FixBox cobra solo 10% de comisión. ¡Pagando por la App obtienes 30 días de garantía!",
    verified: "Verificado",
    c_p1: "Pendientes", c_p2: "En Proceso", c_p3: "Finalizados", w_p1: "Mercado", w_p2: "Mis Activos", w_p3: "Historial",
    btn_grab: "¡Tomar Pedido!", btn_start: "Iniciar Trabajo", btn_finish: "Finalizar Trabajo", btn_approve: "Aprobar y Pagar",
    lbl_bank: "Banco General", locked_grab: "Completa tu perfil para tomar pedidos",
    lbl_fn: "Nombre", lbl_ln: "Apellido", lbl_wa: "WhatsApp", lbl_addr: "Dirección detallada",
    lbl_acc_name: "Titular de Cuenta (Igual a Cédula)", lbl_acc_num: "Número de Cuenta",
    lbl_ref: "Contacto de Emergencia (Nombre y Celular)", lbl_id: "Foto Cédula/Pasaporte", lbl_selfie: "Selfie (Rostro Claro)",
    bank_match_err: "ERROR: El titular de la cuenta bancaria debe coincidir EXACTAMENTE con su Nombre y Apellido registrados.",
    privacy_notice: "Toda tu información personal y bancaria está encriptada y es estrictamente confidencial.",
    bank_notice: "Requerido de forma exclusiva para garantizar que recibas tus pagos de forma inmediata y sin comisiones de otros bancos.",
    btn_dispute: "Abrir Disputa (Problema)", dispute_prompt: "Por favor, describe detalladamente el problema. Retendremos el pago y revisaremos el caso:",
    admin_refund: "Reembolsar Cliente", admin_pay: "Pagar al Técnico",
    yappy_sim_title: "Conectando con Yappy...", yappy_sim_desc: "Por favor, no cierres esta ventana. Procesando pago seguro.",
    login_title: "Iniciar Sesión", login_desc: "Ingresa tu celular y contraseña para continuar.",
    login_pwd: "Contraseña (Mínimo 6 caracteres)",
    reg_pwd_confirm: "Confirmar contraseña", pwd_match_err: "Las contraseñas no coinciden.",
    btn_login_submit: "Entrar", btn_to_register: "¿No tienes cuenta? Regístrate aquí",
    reg_title: "Crear Cuenta", reg_btn: "Registrarme",
    warm_tip: "El técnico ha terminado. Si está satisfecho, por favor apruebe el pago para activar sus 30 días de garantía.",
    auto_claim_btn: "Reclamar Pago",
    dispute_reason_label: "Razón de la disputa:",
    skills_label: "Especialidades", select_skills: "Selecciona los servicios que ofreces:",
    addr_placeholder: "Ej: Bella Vista... (O puedes pegar el link de Waze/Maps aquí)",
    share_title: "¡Gana $5 por cada amigo!", share_desc: "Invita a un amigo a FixBox. Si realiza un pedido, ¡ambos reciben $5 de saldo!", share_btn: "Compartir ahora",
    promo_placeholder: "Código de Descuento", promo_btn: "Aplicar", promo_applied: "¡Cupón Aplicado!", promo_err_used: "Ya usaste este cupón.", promo_err_invalid: "Cupón inválido o expirado.",
    warranty_banner: "🛡️ Paga por la App y obtén 30 días de Garantía",
    zone_label: "¿En qué zona necesitas el servicio?", zone_placeholder: "Selecciona tu zona",
    schedule_label: "¿Cuándo lo necesitas?", schedule_today: "Hoy", schedule_tomorrow: "Mañana",
    schedule_slot_label: "Horario preferido", schedule_urgent: "¡Urgente! (Lo antes posible)",
    coverage_label: "Zonas de cobertura", coverage_desc: "Selecciona las zonas donde puedes trabajar",
    zone_required: "Por favor selecciona tu zona de servicio.",
    referral_title: "¡Gana $5 por cada amigo!",
    referral_desc: "Comparte tu código. Cuando tu amigo complete su primer pedido, ambos reciben $5 en su billetera.",
    referral_code_label: "Tu código de invitación",
    referral_input_label: "¿Tienes un código de invitación? (Opcional)",
    referral_share_btn: "Invitar por WhatsApp",
    referral_copy_btn: "Copiar código",
    referral_copied: "¡Código copiado!",
    referral_invalid: "Código de invitación inválido."
  },
  zh: {
    appName: "FixBox", home: "首页", orders: "订单", profile: "个人中心",
    wallet: "我的钱包", balance: "可提现/消费余额", withdraw: "申请提现",
    withdraw_success: "申请成功，等待打款。", withdraw_hist: "资金明细",
    edit_profile: "完善资料", email: "登录账号", phone: "WhatsApp", address: "详细住址",
    save: "保存修改", rules: "平台规则与售后保障", price_label: "价格:", support_btn: "客服",
    btn_order: "去支付", pay_select: "支付方式", pay_yappy: "Yappy 支付 (全自动)", pay_wallet: "钱包余额支付",
    pay_done: "确认支付", photos_title: "存证照片 (最多6张)",
    suggest_title: "您还需要什么定制服务？", suggest_btn: "提交需求", suggest_success: "感谢！我们已收到您的留言需求，客服会尽快联系您。",
    legal_p: "平台仅收 10% 佣金。通过 App 支付即可享受 30 天超长保修！",
    verified: "已认证",
    c_p1: "待处理", c_p2: "进行中", c_p3: "已完成", w_p1: "抢单大厅", w_p2: "我的任务", w_p3: "已完成",
    btn_grab: "立即抢单！", btn_start: "开始工作", btn_finish: "完工打卡", btn_approve: "确认验收并付款",
    lbl_bank: "Banco General", locked_grab: "请先完善实名与收款账号",
    lbl_fn: "名字", lbl_ln: "姓氏", lbl_wa: "WhatsApp电话", lbl_addr: "详细住址",
    lbl_acc_name: "账户姓名 (必须与证件完全一致)", lbl_acc_num: "账号号码",
    lbl_ref: "紧急联系人 (姓名与手机号)", lbl_id: "拍身份证/护照", lbl_selfie: "本人清晰自拍",
    bank_match_err: "安全拦截：银行账户姓名必须与您填写的【名字+姓氏】完全一致！",
    privacy_notice: "您的所有个人信息及银行资料均受到最高级别的加密保护与严格保密。",
    bank_notice: "平台仅限绑定 Banco General，以此确保您的款项秒到账，且不产生任何跨行手续费。",
    btn_dispute: "发起维权申诉", dispute_prompt: "请详细描述您遇到的问题，平台将立即冻结资金并介入调查：",
    admin_refund: "判给客户 (全额退款)", admin_pay: "判给师傅 (强制打款)",
    yappy_sim_title: "正在连接 Yappy 支付...", yappy_sim_desc: "请勿关闭此页面，正在进行银行级加密处理。",
    login_title: "账号登录", login_desc: "请输入您的手机号与密码以继续。",
    login_pwd: "请输入密码 (至少6位)",
    reg_pwd_confirm: "请再次输入密码确认", pwd_match_err: "两次输入的密码不一致。",
    btn_login_submit: "立即登录", btn_to_register: "没有账号？点击这里注册",
    reg_title: "创建新账号", reg_btn: "完成注册",
    warm_tip: "师傅已完工，请验收！在线支付即可激活 30 天官方售后保障，修不好平台赔！",
    auto_claim_btn: "申请超时强制收款",
    dispute_reason_label: "客户申诉原因：",
    skills_label: "专业技能", select_skills: "请勾选您可以接单的服务类型：",
    addr_placeholder: "例如：Costa大楼... (也可直接粘贴 Waze 定位链接)",
    share_title: "邀请好友，各得 $5.00", share_desc: "分享给邻居或好友，当他们完成首单后，你们的钱包都将获得 $5.00 奖励！", share_btn: "立即分享赚钱",
    promo_placeholder: "输入优惠口令", promo_btn: "兑换", promo_applied: "优惠券已生效！", promo_err_used: "您已经使用过该口令了。", promo_err_invalid: "口令无效或已过期。",
    warranty_banner: "🛡️ 平台支付尊享 30 天官方保修 (售后无忧)",
    zone_label: "服务地址在哪个区域？", zone_placeholder: "选择服务区域",
    schedule_label: "您需要什么时候上门？", schedule_today: "今天", schedule_tomorrow: "明天",
    schedule_slot_label: "偏好时间段", schedule_urgent: "紧急！（越快越好）",
    coverage_label: "服务覆盖范围", coverage_desc: "选择您可以接单的区域",
    zone_required: "请选择服务区域。",
    referral_title: "推荐好友各得 $5！",
    referral_desc: "分享你的专属码。好友完成首单后，你和好友各得 $5 钱包余额。",
    referral_code_label: "你的专属邀请码",
    referral_input_label: "有邀请码吗？（选填）",
    referral_share_btn: "WhatsApp 分享",
    referral_copy_btn: "复制邀请码",
    referral_copied: "已复制！",
    referral_invalid: "邀请码无效。"
  }
};

// ---- 设计 token ----
export const v2 = {
  colors: {
    bg: '#F4F7FA', surface: '#FFFFFF', ink: '#17202A', muted: '#697586', line: '#E3E8EF',
    green: '#16A34A', blue: '#2563EB', gold: '#B8860B', red: '#DC2626',
    softGreen: '#ECFDF3', softBlue: '#EFF6FF', softGold: '#FFF7E6', softRed: '#FEF2F2'
  },
  card: { background: '#FFFFFF', border: '1px solid #E3E8EF', borderRadius: '8px', boxShadow: '0 6px 18px rgba(15, 23, 42, 0.05)' },
  field: { width: '100%', padding: '13px 14px', borderRadius: '8px', border: '1px solid #D8DEE8', backgroundColor: '#FFFFFF', boxSizing: 'border-box', fontSize: '15px', outline: 'none' }
};

// ---- 师傅审核状态工具 ----
export const getWorkerReviewStatus = (worker = {}) => {
  if (worker.isVerified) return 'APPROVED';
  if (worker.verification_status === 'REJECTED') return 'REJECTED';
  const ready = [
    Boolean(worker.firstName && worker.lastName && worker.phone && worker.address),
    Boolean(worker.skills && worker.skills.length > 0),
    Boolean(worker.invoice_terms_accepted),
    Boolean(worker.bankInfo?.accName && worker.bankInfo?.accNum && worker.bankInfo?.accType),
    Boolean(worker.idPhoto && worker.selfie)
  ].every(Boolean);
  if (ready || worker.verification_status === 'PENDING') return 'PENDING';
  return 'INCOMPLETE';
};

export const getWorkerReviewTone = (status) => {
  if (status === 'APPROVED') return { bg: '#ECFDF3', color: '#15803D', border: '#BBF7D0' };
  if (status === 'REJECTED') return { bg: '#FEF2F2', color: '#B91C1C', border: '#FECACA' };
  if (status === 'PENDING') return { bg: '#FFF7E6', color: '#B45309', border: '#FCD34D' };
  return { bg: '#F8FAFC', color: '#697586', border: '#E3E8EF' };
};

// 弃单惩罚状态：ok / warning / cooldown / suspended
export const getWorkerPenaltyStatus = (worker = {}) => {
  const count = worker.abandon_count || 0;
  if (worker.is_suspended || count >= 10) return { type: 'suspended', count };
  if (count >= 5) {
    const lastAbandon = worker.last_abandonAt?.toDate?.() || null;
    if (lastAbandon) {
      const hoursElapsed = (Date.now() - lastAbandon.getTime()) / (1000 * 60 * 60);
      if (hoursElapsed < 24) return { type: 'cooldown', count, remaining: Math.ceil(24 - hoursElapsed) };
    }
  }
  if (count >= 3) return { type: 'warning', count };
  return { type: 'ok', count };
};

export const getStatusTone = (status) => {
  if (status === 'COMPLETED') return { bg: '#ECFDF3', color: '#15803D', border: '#BBF7D0' };
  if (status === 'REFUNDED' || status === 'DISPUTED' || status === 'CANCELLED_TIMEOUT') return { bg: '#FEF2F2', color: '#B91C1C', border: '#FECACA' };
  if (status === 'OPEN' || status === 'AWAITING_VERIFICATION' || status === 'PENDING_PAYMENT') return { bg: '#EFF6FF', color: '#1D4ED8', border: '#BFDBFE' };
  return { bg: '#FFF7E6', color: '#B45309', border: '#FCD34D' };
};
